<template>
  <div class="pt-6">
    <v-alert v-if="loadError" type="error" dense text>
      {{ t('loadError') }}
      <template v-slot:append>
        <v-btn text small color="error" @click="$emit('retry')">{{ $t('documentSettings.reload') }}</v-btn>
      </template>
    </v-alert>
    <v-progress-linear v-if="loading" indeterminate :aria-label="t('loading')" />
    <v-alert v-if="removed.length" type="warning" outlined role="status">
      {{ t('removed', { columns: removed.join(', ') }) }}
    </v-alert>
    <v-form @submit.prevent>
      <v-row>
        <v-col cols="12" md="6">
          <!-- Roles read as a summary, like every other allowed-roles select of the app and like the
               mockup; the type filters below keep their chips, which the mockup gives them too. -->
          <v-select
            v-model="draft.menu_roles"
            :items="roleOptions"
            :label="t('menuRoles')"
            :hint="t('menuHint')"
            persistent-hint
            multiple
            outlined
            dense
            :disabled="busy"
          />
        </v-col>
      </v-row>
      <h2 class="text-subtitle-1 font-weight-medium mt-4">{{ t('columns') }}</h2>
      <v-row>
        <v-col cols="12" lg="6">
          <v-card outlined height="100%" tag="fieldset" class="column-panel">
            <legend class="column-panel__legend text-caption text-uppercase text--secondary">
              {{ t('available') }}
            </legend>
            <v-list dense>
              <template v-for="group in columnGroups">
                <v-subheader :key="group.title">{{ t(group.title) }}</v-subheader>
                <template v-for="(column, index) in group.columns">
                  <v-list-item :key="column.key">
                    <v-list-item-content>
                      <v-list-item-title>{{ label(column) }}</v-list-item-title>
                    </v-list-item-content>
                    <!-- The badges stay on the row, between the label and the action, as in the
                         mockup. One badge lists the types rather than one per type: the registry tab
                         reads them the same way, joined, and four pills would wrap the row. -->
                    <v-chip v-if="column.key === 'name'" x-small label class="ml-2 flex-shrink-0">
                      {{ t('required') }}
                    </v-chip>
                    <v-chip v-if="columnTypes(column.key)" x-small label class="ml-2 flex-shrink-0">
                      {{ columnTypes(column.key) }}
                    </v-chip>
                    <!-- A plain flex box, as in the other panel: v-list-item-action carries a 12px
                         vertical margin that `dense` does not reduce, which made these rows 52px tall
                         against the other panel's 40. -->
                    <div class="d-flex flex-shrink-0 ml-2">
                      <v-btn
                        icon
                        small
                        :disabled="busy"
                        :aria-label="t('addAriaLabel', { column: label(column) })"
                        @click="add(column.key)"
                      >
                        <v-icon small>mdi-plus</v-icon>
                      </v-btn>
                    </div>
                  </v-list-item>
                  <v-divider v-if="index < group.columns.length - 1" :key="column.key + '-divider'" />
                </template>
                <!-- Not while loading: `available` is still empty then, and the group would claim the
                     tenant has no custom field before the answer says so. -->
                <v-list-item v-if="!group.columns.length && !loading" :key="group.title + '-empty'">
                  <v-list-item-content class="text--secondary">{{ t(group.empty) }}</v-list-item-content>
                </v-list-item>
              </template>
            </v-list>
          </v-card>
        </v-col>
        <v-col cols="12" lg="6">
          <v-card outlined height="100%" tag="fieldset" class="column-panel">
            <legend class="column-panel__legend text-caption text-uppercase text--secondary">
              {{ t('selected') }}
            </legend>
            <v-list dense>
              <!-- Dragging is always on, through a handle: a mode to switch on first would cost the
                   click this is meant to save, and the row holds no input a drag could interfere
                   with. The arrows stay as the only ordering a keyboard can reach. -->
              <draggable
                v-model="draft.list.columns"
                handle=".column-handle"
                ghost-class="column-ghost"
                :disabled="busy"
              >
                <v-list-item v-for="(key, index) in draft.list.columns" :key="key">
                  <v-icon class="column-handle mr-2" :class="{ 'column-handle--idle': busy }" :title="t('dragHint')">
                    mdi-drag
                  </v-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ index + 1 }}. {{ columnLabel(key) }}</v-list-item-title>
                  </v-list-item-content>
                  <!-- The types follow the column into this panel: which documents a column applies
                       to is what tells "Warranty duration" apart from a column every row fills. -->
                  <v-chip v-if="key === 'name'" x-small label class="ml-2 flex-shrink-0">
                    {{ t('required') }}
                  </v-chip>
                  <v-chip v-if="columnTypes(key)" x-small label class="ml-2 flex-shrink-0">
                    {{ columnTypes(key) }}
                  </v-chip>
                  <div class="d-flex flex-shrink-0">
                    <v-btn
                      icon
                      small
                      :disabled="busy || index === 0"
                      :aria-label="t('upAriaLabel', { column: columnLabel(key) })"
                      @click="move(index, -1)"
                    >
                      <v-icon small>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      :disabled="busy || index === draft.list.columns.length - 1"
                      :aria-label="t('downAriaLabel', { column: columnLabel(key) })"
                      @click="move(index, 1)"
                    >
                      <v-icon small>mdi-arrow-down</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      :disabled="busy || key === 'name'"
                      :aria-label="t('removeAriaLabel', { column: columnLabel(key) })"
                      @click="remove(key)"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </v-list-item>
              </draggable>
            </v-list>
            <p v-if="columnsError" class="error--text px-4" role="alert">{{ t(columnsError) }}</p>
          </v-card>
        </v-col>
      </v-row>
      <h2 class="text-subtitle-1 font-weight-medium mt-4">{{ t('filters') }}</h2>
      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="draft.list.default_filters.document_types"
            :items="documentOptions"
            :label="t('documentTypes')"
            :hint="t('allDocuments')"
            persistent-hint
            multiple
            small-chips
            deletable-chips
            outlined
            dense
            :disabled="busy"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="draft.list.default_filters.entity_types"
            :items="entityOptions"
            :label="t('entityTypes')"
            :hint="t('allEntities')"
            persistent-hint
            multiple
            small-chips
            deletable-chips
            outlined
            dense
            :disabled="busy"
          />
        </v-col>
      </v-row>
      <p class="text-body-2 text--secondary">{{ t('filtersHint') }}</p>
    </v-form>
  </div>
</template>

<script>
  import draggable from 'vuedraggable'
  import { DOCUMENT_FIELD_TYPES } from '@/helpers/documentCustomFields'
  import {
    defaultDocumentDisplay,
    documentCustomFieldLabel,
    documentDisplayPayload,
    DOCUMENT_MENU_ROLES,
    STANDARD_DOCUMENT_COLUMNS,
    DOCUMENT_ENTITY_FILTER_VALUES,
    DOCUMENT_ENTITY_LABELS,
  } from '@/helpers/documentDisplay'

  export default {
    name: 'DocumentDisplaySettings',
    components: { draggable },
    props: {
      settings: { type: Object, default: null },
      // The registry draft, so a key activated on the other tab can be picked as a column right away:
      // both sections travel in the same save, and the api validates them together.
      draftFields: { type: Array, default: null },
      busy: { type: Boolean, default: false },
      columnsError: { type: String, default: '' },
      committed: { type: Number, default: 0 },
      loading: { type: Boolean, default: false },
      loadError: { type: Boolean, default: false },
    },
    data() {
      // The baseline starts on the defaults the draft starts on, not empty: the settings only arrive
      // later, and until then an untouched screen would announce work nobody had done.
      const draft = documentDisplayPayload(defaultDocumentDisplay())
      return {
        draft,
        // The document as it was handed down or last saved, to tell an untouched screen from one
        // holding work the admin has not written yet.
        saved: JSON.stringify(draft),
        // Fields the api answered with: active and already stored.
        serverFields: [],
        removed: [],
      }
    },
    computed: {
      // What may be picked as a column: what the api knows, plus what the registry draft has just
      // activated. The latter is not stored yet, but it will be written by the same save.
      available() {
        // The draft wins over the answer, both ways: a stored key it deactivated must leave the list,
        // not only a new one it activated. Standard columns stay, documentFieldErrors refusing their keys.
        const drafted = new Map((this.draftFields || []).map((field) => [field.key, field]))
        const fields = this.serverFields.filter((field) => drafted.get(field.key)?.active !== false)
        const known = new Set(fields.map((field) => field.key))
        for (const field of drafted.values()) {
          if (field.active !== false && !known.has(field.key)) {
            fields.push(field)
          }
        }
        return fields
      },
      dirty() {
        return JSON.stringify(this.draft) !== this.saved
      },
      roleOptions() {
        return DOCUMENT_MENU_ROLES.map((value) => ({ value, text: this.t('roles.' + value) }))
      },
      documentOptions() {
        return DOCUMENT_FIELD_TYPES.map((value) => ({ value, text: this.$t('documentSettings.types.' + value) }))
      },
      entityOptions() {
        // "None" is stored as DOCUMENT_NO_ENTITY and can be combined with the other types.
        return DOCUMENT_ENTITY_FILTER_VALUES.map((value) => ({ value, text: this.$t(DOCUMENT_ENTITY_LABELS[value]) }))
      },
      // A selected column leaves the available list and comes back when it is removed, so a key is
      // never offered on one side while it sits on the other.
      columnGroups() {
        const selected = new Set(this.draft.list.columns)
        return [
          { title: 'standard', standard: true },
          { title: 'custom', standard: false },
        ].map(({ title, standard }) => {
          const group = this.available.filter((c) => STANDARD_DOCUMENT_COLUMNS.includes(c.key) === standard)
          return {
            title,
            columns: group.filter((c) => !selected.has(c.key)),
            // Having nothing left to add is not having nothing to offer: the custom group is empty on
            // a tenant that defined no custom field, and that reads differently.
            empty: group.length ? 'allSelected' : 'noCustom',
          }
        })
      },
    },
    watch: {
      // The registry tab refuses to deactivate a key selected as a column, and a selection made here
      // counts from the click that made it, not from the save: otherwise deactivating passes, the api
      // agrees since it never saw the column, and the reload silently drops it from this draft.
      'draft.list.columns': {
        immediate: true,
        handler(columns) {
          this.$emit('columns', [...columns])
        },
      },
      // The view owns the call; this rebuilds the editable draft each time it hands down a fresh
      // document. On a reload after someone else's save, reconcileColumns is what notices a selected
      // column whose key has since been deactivated.
      settings: {
        immediate: true,
        handler(settings) {
          if (!settings) {
            return
          }
          if (this.serverFields.length) {
            this.reconcileColumns(settings)
            return
          }
          this.draft = documentDisplayPayload(settings)
          this.saved = JSON.stringify(this.draft)
          this.serverFields = settings.available_fields
          this.removed = []
        },
      },
      // A key deactivated or dropped on the registry tab must leave the selection here as well, or
      // the save would carry a column the api refuses with columns_unknown_key.
      draftFields() {
        // Not before the document lands: the registry tab mounts on its first opening and announces an
        // empty draft, which would filter the columns against a list the answer has not filled yet.
        if (!this.draftFields || !this.serverFields.length) {
          return
        }
        const allowed = new Set(this.available.map((column) => column.key))
        this.draft.list.columns = this.draft.list.columns.filter((key) => allowed.has(key))
      },
      // The write happened; the baseline follows it, since the reload goes through reconcileColumns
      // and leaves the draft alone.
      committed() {
        this.saved = JSON.stringify(this.draft)
      },
      // The view carries the payload into the single save.
      draft: {
        deep: true,
        immediate: true,
        handler(draft) {
          this.$emit('change', documentDisplayPayload(draft))
        },
      },
      // The view shows which tab holds unsaved work, including the one that is not on screen.
      dirty: {
        immediate: true,
        handler(dirty) {
          this.$emit('dirty', dirty)
        },
      },
    },
    methods: {
      t(key, params) {
        return this.$t('documentDisplay.' + key, params)
      },
      // Standard keys reach `available_fields` with their key alone and are translated here; a
      // custom one carries the tenant labels and goes through the same fallback chain as the list.
      label(field) {
        return STANDARD_DOCUMENT_COLUMNS.includes(field.key)
          ? this.t('standardLabels.' + field.key)
          : documentCustomFieldLabel(field, this.$i18n.locale)
      },
      columnLabel(key) {
        return this.label(this.available.find((c) => c.key === key) || { key })
      },
      // The document types a column carries values for, joined as the registry tab joins them. Read
      // from `available` rather than from the row, so it answers on both panels.
      columnTypes(key) {
        const types = this.available.find((c) => c.key === key)?.document_types || []
        return types.map((type) => this.$t('documentSettings.types.' + type)).join(', ')
      },
      add(key) {
        if (!this.busy && !this.draft.list.columns.includes(key)) {
          this.draft.list.columns.push(key)
        }
      },
      remove(key) {
        if (!this.busy && key !== 'name') {
          this.draft.list.columns = this.draft.list.columns.filter((c) => c !== key)
        }
      },
      move(index, offset) {
        const next = index + offset
        if (this.busy || next < 0 || next >= this.draft.list.columns.length) {
          return
        }
        const columns = [...this.draft.list.columns]
        columns.splice(next, 0, columns.splice(index, 1)[0])
        this.draft.list.columns = columns
      },
      reconcileColumns(settings) {
        this.serverFields = settings.available_fields
        const allowed = new Set(this.available.map((column) => column.key))
        const removed = this.draft.list.columns.filter((key) => !allowed.has(key))
        this.removed = [...new Set([...this.removed, ...removed.map(this.columnLabel)])]
        this.draft.list.columns = this.draft.list.columns.filter((key) => allowed.has(key))
      },
    },
  }
</script>

<style scoped>
  /* The panel title sits in the border, as in the mockup and as the outlined selects of this same
     form already do. A fieldset gets that from the browser, which cuts its border for the legend —
     the mechanism Vuetify itself uses for those inputs — so it needs no opaque background of its own
     and follows the theme and the partner colours like any other card. */
  .column-panel {
    min-width: 0;
    padding: 0 0 8px;
  }
  .column-panel__legend {
    /* Vuetify's reset sets `display: table` on every legend, and the border is only cut for a legend
       the browser lays out natively. */
    display: block;
    /* text-caption carries a 20px line height; the notch in the border is as tall as the line, so it
       follows the text rather than the class. */
    line-height: 1.2;
    margin-left: 10px;
    padding: 0 5px;
  }
  .column-handle {
    cursor: move;
  }
  /* Saving and loading disable every control of the row; the handle follows, and sortablejs is
     already told not to start a drag through the `disabled` prop. */
  .column-handle--idle {
    cursor: default;
    opacity: 0.4;
  }
  .column-ghost {
    opacity: 0.5;
  }
</style>
